import React, { useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Form, Modal, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { Typography } from 'modules/core/components'
import configuration from 'modules/configuration'
import { notifyMappingError } from '../../../utils'

const MapRowModal = ({
  visible,
  onClose,
  onRefetch,
  mapping,
  unmappedSources,
  onSearchSources,
  onLoadSources,
  documentTypes,
  editingRow,
  valuesByTarget,
  createDimensionMappingRow,
  editDimensionMappingRow,
}) => {
  const [form] = Form.useForm()
  const [isSaving, setIsSaving] = useState(false)
  const [documentType, setDocumentType] = useState(null)
  const [restricts, setRestricts] = useState(false)
  const seededFor = useRef(null)
  const { t } = useTranslation()

  const sourceLabel = mapping?.labels?.[mapping?.source_dimension] ?? ''
  const isSubtype = mapping?.is_subtype

  // Una fila sin ningún valor no restringiría nada, o sea que sería lo mismo que no tenerla.
  const hasAnyValue = useCallback(
    (values) => (mapping?.targets ?? []).some((target) => values[target]?.length),
    [mapping],
  )

  // Editando, el origen ya está decidido: se precargan sus conjuntos y no se elige tipo ni subtipo.
  // Se siembra una sola vez por apertura: si el efecto se repite, no pisa lo que se está eligiendo.
  useEffect(() => {
    if (!visible || !editingRow) {
      seededFor.current = null
      return
    }
    if (seededFor.current === editingRow.source.id) return
    seededFor.current = editingRow.source.id

    const values = Object.fromEntries(
      (mapping?.targets ?? []).map((target) => [
        target,
        editingRow.values[target].map((value) => value.id),
      ]),
    )
    form.setFieldsValue({ source_value_id: editingRow.source.id, ...values })
    setRestricts(hasAnyValue(values))
  }, [visible, editingRow, mapping, form, hasAnyValue])

  // Con un solo tipo no hay nada que elegir: queda puesto y deshabilitado, como en el alta de DPD.
  useEffect(() => {
    if (!visible || editingRow || !isSubtype || documentTypes.length !== 1) return
    const onlyType = documentTypes[0].id
    form.setFieldsValue({ document_type: onlyType })
    setDocumentType(onlyType)
    onLoadSources('', onlyType)
  }, [visible, editingRow, isSubtype, documentTypes, form, onLoadSources])

  const handleClose = () => {
    form.resetFields()
    setDocumentType(null)
    setRestricts(false)
    setIsSaving(false)
    onClose()
  }

  const handleDocumentTypeChange = (value) => {
    setDocumentType(value)
    form.setFieldsValue({ source_value_id: undefined })
    onLoadSources('', value)
  }

  const handleSubmit = () => {
    form.validateFields().then(({ source_value_id, document_type, ...selected }) => {
      const values = isSubtype
        ? Object.fromEntries(Object.entries(selected).filter(([, ids]) => ids?.length))
        : selected
      setIsSaving(true)
      const request = editingRow
        ? editDimensionMappingRow(mapping.source_dimension, source_value_id, { values })
        : createDimensionMappingRow(mapping.source_dimension, { source_value_id, values })

      request
        .then(() => {
          onRefetch()
          handleClose()
        })
        .catch((error) => {
          setIsSaving(false)
          notifyMappingError(error)
        })
    })
  }

  const toOptions = (values = []) =>
    values.map((value) => ({
      value: value.id,
      label: `${value.code_destination} - ${value.name_destination}`,
    }))

  const sourceOptions = editingRow
    ? [
        {
          value: editingRow.source.id,
          label: `${editingRow.source.code} - ${editingRow.source.name}`,
        },
      ]
    : toOptions(unmappedSources)

  return (
    <Modal
      title={t(
        editingRow
          ? 'CONFIG_DIMENSION_MAPPING_ROW_EDIT_MODAL_TITLE'
          : 'CONFIG_DIMENSION_MAPPING_ROW_MODAL_TITLE',
        { source: sourceLabel },
      )}
      visible={visible}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={t('ACTION_SAVE')}
      cancelText={t('ACTION_CANCEL')}
      okButtonProps={{ disabled: isSubtype && !restricts }}
      confirmLoading={isSaving}
      destroyOnClose
      centered
      forceRender
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={(_, allValues) => setRestricts(hasAnyValue(allValues))}
      >
        {isSubtype && (
          <Typography.Body level={3}>
            {t('CONFIG_DIMENSION_MAPPING_SUBTYPE_TARGETS_HELP')}
          </Typography.Body>
        )}
        {isSubtype && !editingRow && (
          <Form.Item
            name="document_type"
            label={t('FIELD_DOCUMENT_TYPE')}
            rules={[{ required: true, message: t('REQUIRED_FIELD') }]}
          >
            <Select
              options={documentTypes.map((type) => ({ value: type.id, label: type.name }))}
              placeholder={t('ACTION_SELECT')}
              disabled={documentTypes.length === 1}
              onChange={handleDocumentTypeChange}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        )}
        <Form.Item
          name="source_value_id"
          label={sourceLabel}
          rules={[{ required: true, message: t('REQUIRED_FIELD') }]}
        >
          {/* Solo los no mapeados: el origen queda bloqueado al mapearse */}
          <Select
            options={sourceOptions}
            placeholder={t('ACTION_SELECT')}
            disabled={Boolean(editingRow) || (isSubtype && !documentType)}
            onSearch={(value) => onSearchSources(value, documentType)}
            filterOption={false}
            showSearch
          />
        </Form.Item>
        {(mapping?.targets ?? []).map((target) => (
          <Form.Item
            key={target}
            name={target}
            label={mapping.labels?.[target] ?? target}
            rules={isSubtype ? [] : [{ required: true, message: t('REQUIRED_FIELD') }]}
          >
            <Select
              mode={isSubtype ? 'multiple' : undefined}
              options={toOptions(valuesByTarget[target])}
              placeholder={t('ACTION_SELECT')}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        ))}
      </Form>
    </Modal>
  )
}

const mapDispatchToProps = {
  createDimensionMappingRow: configuration.actions.createDimensionMappingRow,
  editDimensionMappingRow: configuration.actions.editDimensionMappingRow,
}

export default connect(null, mapDispatchToProps)(MapRowModal)
